"use client";

import type { CSSProperties } from "react";
import { cn } from "@/app/lib/utils/cn";

interface SkeletonProps {
  className?: string;
  variant?: "text" | "circular" | "rectangular" | "card";
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export default function Skeleton({
  className,
  variant = "rectangular",
  width,
  height,
  lines = 1,
}: SkeletonProps) {
  const baseClasses = "animate-pulse bg-gradient-to-r from-(--purple)/10 via-(--pink)/20 to-(--purple)/10 bg-[length:200%_100%]";
  
  const variantClasses = {
    text: "h-4 rounded",
    circular: "rounded-full",
    rectangular: "rounded-lg",
    card: "rounded-xl",
  };

  const style: CSSProperties = {
    width: width ?? "100%",
    height: height ?? (variant === "text" ? "1rem" : variant === "circular" ? width : "auto"),
  };

  if (variant === "text" && lines > 1) {
    return (
      <div className={cn("space-y-2", className)}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(baseClasses, variantClasses.text)}
            style={{ 
              width: i === lines - 1 ? "75%" : "100%",
              height: "1rem"
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      style={style}
    />
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("theme-card-alt rounded-xl p-6 shadow-sm", className)}>
      <Skeleton variant="rectangular" height={160} className="mb-4" />
      <Skeleton variant="text" className="mb-2" />
      <Skeleton variant="text" width="60%" />
    </div>
  );
}

export function SkeletonStatCard({ className }: { className?: string }) {
  return (
    <div className={cn("theme-card-alt rounded-xl p-5 shadow-sm text-center", className)}>
      <Skeleton variant="text" height={32} width="60%" className="mx-auto mb-2" />
      <Skeleton variant="text" height={16} width="80%" className="mx-auto" />
    </div>
  );
}
